import * as React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/app/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/app/DataTable';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ErrorState } from '@/components/app/ErrorState';
import { humanizeError } from '@/lib/apiClient';
import { useAlertFirings, useAlertPolicies } from '../hooks';
import { SeverityBadge } from '../components/SeverityBadge';
import type { AlertSeverity, NotificationAlertFiring } from '../types';

export function AlertsFiringsPage() {
  const [severity, setSeverity] = React.useState<AlertSeverity | 'all'>('all');
  const [policyKey, setPolicyKey] = React.useState<string>('all');
  // Stored as the raw <input type="datetime-local"> value (local time, "YYYY-MM-DDTHH:mm").
  // Converted to UTC ISO when building query filters.
  const [sinceLocal, setSinceLocal] = React.useState<string>('');
  const navigate = useNavigate();

  const policies = useAlertPolicies();
  const filters = React.useMemo(
    () => ({
      severity: severity === 'all' ? undefined : severity,
      policyKey: policyKey === 'all' ? undefined : policyKey,
      since: sinceLocal ? new Date(sinceLocal).toISOString() : undefined,
      limit: 100,
    }),
    [severity, policyKey, sinceLocal],
  );
  const firings = useAlertFirings(filters);

  const columns = React.useMemo<ColumnDef<NotificationAlertFiring>[]>(
    () => [
      {
        accessorKey: 'firedAt',
        header: 'Fired at',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.firedAt}</span>,
      },
      {
        accessorKey: 'policyKey',
        header: 'Policy',
        cell: ({ row }) => <code className="text-xs">{row.original.policyKey}</code>,
      },
      {
        accessorKey: 'severity',
        header: 'Severity',
        cell: ({ row }) => <SeverityBadge severity={row.original.severity} />,
      },
      {
        id: 'observed',
        header: 'Observed vs threshold',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {row.original.observedValue} / {row.original.threshold}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          if (row.original.resolvedAt) return <Badge variant="success">Resolved</Badge>;
          if (row.original.acknowledgedAt) return <Badge variant="muted">Ack</Badge>;
          return <Badge variant="destructive">Open</Badge>;
        },
      },
      {
        id: 'dispatch',
        header: 'Dispatched',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.dispatchedTo.map((c) => (
              <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>
            ))}
            {row.original.dispatchFailures && row.original.dispatchFailures.length > 0 && (
              <Badge variant="destructive" className="text-[10px]">dispatch errors</Badge>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alerts · Firings"
        description="Alert breaches observed by AlertEvaluatorCron. Click a row for snapshot and ack."
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 p-4">
          <div className="space-y-1.5">
            <Label>Severity</Label>
            <Select value={severity} onValueChange={(v) => setSeverity(v as AlertSeverity | 'all')}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Policy</Label>
            <Select value={policyKey} onValueChange={setPolicyKey}>
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {(policies.data?.policies ?? []).map((p) => (
                  <SelectItem key={p.policyKey} value={p.policyKey}>{p.displayName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Since</Label>
            <Input
              type="datetime-local"
              value={sinceLocal}
              onChange={(e) => setSinceLocal(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {firings.isError && (
        <ErrorState
          title="Firings unavailable"
          description={humanizeError(firings.error)}
          actionLabel="Retry"
          onAction={() => firings.refetch()}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Firings</CardTitle>
          <CardDescription>Auto-refresh every 60 seconds.</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable<NotificationAlertFiring>
            columns={columns}
            data={firings.data?.firings ?? []}
            loading={firings.isLoading}
            rowKey={(r) => r.firingId}
            emptyLabel="No firings in window"
            emptyDescription="Everything is healthy, or your filters exclude all records."
            onRowClick={(row) =>
              navigate(`/notifications/alerts/firings/${encodeURIComponent(row.firingId)}`)
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
